import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { Check, Pencil, Clock3, X, Sparkles, RefreshCw, ArrowRight } from 'lucide-react';

const CONF_STYLE = {
  alta: 'bg-green-100 text-green-700',
  'média': 'bg-amber-100 text-amber-700',
  baixa: 'bg-red-100 text-red-700',
};

const TYPE_META = {
  activation: { label: 'Ativação', cls: 'bg-blue-100 text-blue-700' },
  params_update: { label: 'Atualização', cls: 'bg-violet-100 text-violet-700' },
};

const FIELDS = [
  ['reorder_point', 'Reorder point'],
  ['safety_stock', 'Safety stock'],
  ['optimal_stock', 'Stock ótimo'],
  ['moq', 'MOQ'],
  ['lead_time_days', 'Lead time (d)'],
];

const fmtVal = (v) => (v === null || v === undefined ? '—' : v);

export const AiSuggestions = ({ onCountChange }) => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [recalcing, setRecalcing] = useState(false);
  const [editSug, setEditSug] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [rejectSug, setRejectSug] = useState(null);
  const [note, setNote] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/procurement/suggestions?status=pending`);
      setItems(res.data.suggestions || []);
      setCounts(res.data.counts || {});
      onCountChange?.(res.data.counts?.pending || 0);
    } catch (e) {
      toast.error('Erro ao carregar sugestões AI');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const decide = async (sug, action, extra = {}) => {
    setBusy((b) => ({ ...b, [sug.id]: true }));
    try {
      await axios.post(`${API}/procurement/suggestions/${sug.id}/decide`, { action, ...extra });
      const msg = { accept: 'Sugestão aceite — parâmetros aplicados',
                    edit: 'Valores editados e aplicados',
                    defer: 'Sugestão adiada 30 dias',
                    reject: 'Sugestão rejeitada (não volta a aparecer durante 90 dias)' }[action];
      toast.success(`${sug.sku}: ${msg}`);
      setEditSug(null);
      setRejectSug(null);
      setNote('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao registar decisão');
    } finally {
      setBusy((b) => ({ ...b, [sug.id]: false }));
    }
  };

  const openEdit = (sug) => {
    setEditSug(sug);
    const f = {};
    FIELDS.forEach(([k]) => { f[k] = sug.proposed?.[k] ?? ''; });
    setEditForm(f);
  };

  const submitEdit = () => {
    const values = {};
    for (const [k, v] of Object.entries(editForm)) {
      if (v !== '' && v !== null && !Number.isNaN(Number(v))) values[k] = Number(v);
    }
    decide(editSug, 'edit', { values });
  };

  const recalc = async () => {
    setRecalcing(true);
    toast.info('A recalcular a análise de procura... pode demorar 1-2 minutos.');
    try {
      const res = await axios.post(`${API}/procurement/intelligence/run?mode=suggest`, null, { timeout: 300000 });
      const n = res.data?.report?.totals?.suggestions_created ?? 0;
      toast.success(`Análise concluída — ${n} ${n === 1 ? 'nova sugestão' : 'novas sugestões'}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao recalcular análise');
    } finally {
      setRecalcing(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">A carregar sugestões...</div>;

  return (
    <div className="space-y-4" data-testid="ai-suggestions-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600">
          <Sparkles className="h-4 w-4 inline mr-1 text-violet-600" />
          O AI sugere, tu decides. Nada é aplicado sem a tua aprovação.
          {' '}<span className="text-slate-400">
            ({counts.pending || 0} pendentes · {counts.accepted || 0} aceites · {counts.rejected || 0} rejeitadas)
          </span>
        </p>
        <Button size="sm" variant="outline" onClick={recalc} disabled={recalcing} data-testid="ai-recalc-btn">
          <RefreshCw className={`h-4 w-4 mr-1 ${recalcing ? 'animate-spin' : ''}`} />
          {recalcing ? 'A analisar...' : 'Recalcular análise'}
        </Button>
      </div>

      {items.length === 0 && (
        <Card><CardContent className="py-12 text-center text-slate-500" data-testid="ai-suggestions-empty">
          Sem sugestões pendentes. O AI só sugere quando a diferença é material (&gt;20% no ROP) ou há artigos novos elegíveis.
        </CardContent></Card>
      )}

      <div className="space-y-3">
        {items.map((sug) => {
          const tm = TYPE_META[sug.type] || TYPE_META.params_update;
          return (
            <Card key={sug.id} className="border-slate-200" data-testid={`ai-suggestion-${sug.sku}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-slate-500">{sug.sku}</span>
                      <h3 className="font-semibold text-slate-800 truncate max-w-[380px]">{sug.name || '—'}</h3>
                      <Badge className={`${tm.cls} text-[10px]`}>{tm.label}</Badge>
                      {sug.delta_pct !== null && sug.delta_pct !== undefined && (
                        <Badge variant="outline" className="text-[10px] text-slate-600">Δ {sug.delta_pct}%</Badge>
                      )}
                      {sug.confidence?.label && (
                        <Badge className={`${CONF_STYLE[sug.confidence.label] || CONF_STYLE.baixa} text-[10px]`}>
                          confiança {sug.confidence.label}
                        </Badge>
                      )}
                      {sug.proposed?.abc_class && (
                        <Badge variant="outline" className="text-[10px]">classe {sug.proposed.abc_class}</Badge>
                      )}
                    </div>
                    {sug.supplier && <p className="text-xs text-slate-500 mt-0.5">{sug.supplier}</p>}
                  </div>
                </div>

                <p className="text-sm text-slate-700 bg-violet-50/60 border border-violet-100 rounded-md px-3 py-2">
                  {sug.reason}
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-sm">
                  {FIELDS.map(([k, label]) => {
                    const cur = sug.current?.[k];
                    const prop = sug.proposed?.[k];
                    const changed = cur !== undefined && cur !== null && cur !== prop;
                    return (
                      <div key={k} className="bg-slate-50 rounded p-2">
                        <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
                        <p className="flex items-center gap-1">
                          {changed && <><span className="text-slate-400 line-through">{fmtVal(cur)}</span>
                            <ArrowRight className="h-3 w-3 text-slate-400" /></>}
                          <strong className="text-slate-800">{fmtVal(prop)}</strong>
                        </p>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700"
                          onClick={() => decide(sug, 'accept')} disabled={busy[sug.id]}
                          data-testid={`ai-accept-${sug.sku}`}>
                    <Check className="h-4 w-4 mr-1" /> Aceitar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(sug)} disabled={busy[sug.id]}
                          data-testid={`ai-edit-${sug.sku}`}>
                    <Pencil className="h-4 w-4 mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => decide(sug, 'defer', { defer_days: 30 })}
                          disabled={busy[sug.id]} data-testid={`ai-defer-${sug.sku}`}>
                    <Clock3 className="h-4 w-4 mr-1" /> Adiar 30d
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => { setRejectSug(sug); setNote(''); }} disabled={busy[sug.id]}
                          data-testid={`ai-reject-${sug.sku}`}>
                    <X className="h-4 w-4 mr-1" /> Rejeitar
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editSug} onOpenChange={(o) => !o && setEditSug(null)}>
        <DialogContent className="max-w-md" data-testid="ai-edit-modal">
          <DialogHeader>
            <DialogTitle>Editar e aplicar · {editSug?.sku}</DialogTitle>
            <DialogDescription>Ajusta os valores propostos antes de aplicar ao artigo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {FIELDS.map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs">{label}
                  {editSug?.current?.[k] !== undefined && editSug?.current?.[k] !== null && (
                    <span className="text-slate-400 ml-1">(atual: {editSug.current[k]})</span>
                  )}
                </Label>
                <Input type="number" value={editForm[k]} data-testid={`ai-edit-${k}`}
                       onChange={(e) => setEditForm((f) => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
            <Button className="w-full" onClick={submitEdit} disabled={editSug && busy[editSug.id]}
                    data-testid="ai-edit-save">
              Aplicar valores editados
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectSug} onOpenChange={(o) => !o && setRejectSug(null)}>
        <DialogContent className="max-w-md" data-testid="ai-reject-modal">
          <DialogHeader>
            <DialogTitle>Rejeitar sugestão · {rejectSug?.sku}</DialogTitle>
            <DialogDescription>
              O AI não volta a sugerir este artigo durante 90 dias. Nota opcional (fica no histórico).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="Porquê? (ex: artigo em descontinuação)" data-testid="ai-reject-note" />
            <Button variant="destructive" className="w-full" data-testid="ai-reject-confirm"
                    onClick={() => decide(rejectSug, 'reject', { note: note || undefined })}
                    disabled={rejectSug && busy[rejectSug.id]}>
              Confirmar rejeição
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
